import {uniq} from 'lodash';
import React, {FC, ReactNode, useRef} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {parseUniqueEmailsFromText} from '../../../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {
  BoxedInput,
  BoxedInputProps,
} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {fontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {TokensEditor} from './tokensEditor';

/*
 * Props.
 */

interface EmailsInputProps {
  emails: EmailsInputState;
  onChangeEmails: OnChangeEmails;
  help?: ReactNode;
}

export type OnChangeEmails = (computeEmails: (prevEmails: EmailsInputState) => EmailsInputState) => void;

export interface EmailsInputState {
  text: string;
  tokens: ReadonlyArray<string>;
}

/*
 * Style.
 */

const StyledEmailsInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledBoxedInput = styled(({className, ...props}: {className?: string} & BoxedInputProps) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <BoxedInput {...props} boxClassName={className} />
))`
  min-height: 91px;
  padding: 6.5px 8px;
  display: block;
  margin-bottom: 4px;
`;

const StyledEmailInputTokenDiv = styled.div<{$isSelected: boolean}>`
  margin-right: 4px;
  padding: 3px 6px;
  background-color: ${(p) => (p.$isSelected ? p.theme.greys.shade70 : p.theme.alphas.black30)};
  color: ${(p) => (p.$isSelected ? p.theme.greys.white : p.theme.greys.shade90)};
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: ${fontSizes.medium};
  font-style: normal;
  font-weight: 400;
  line-height: 16px;

  &:hover {
    background-color: ${(p) => (p.$isSelected ? p.theme.greys.shade70 : p.theme.alphas.black40)};
    color: ${(p) => (p.$isSelected ? p.theme.greys.white : p.theme.greys.black)};
  }
`;

/*
 * Component.
 */

export const EmailsInput: FC<EmailsInputProps> = (props) => {
  const {emails, onChangeEmails, help} = props;
  const localize = useLocalize();

  const onRemoveTokensClick = (indicesToRemove: ReadonlyArray<number>) => {
    onChangeEmails((prevEmails) => ({
      ...prevEmails,
      tokens: prevEmails.tokens.filter((_, index) => !indicesToRemove.includes(index)),
    }));
  };

  const onChangeText = (text: string) => {
    const [newTokens, rest] = parseUniqueEmailsFromText(text);

    if (text.includes(' ')) {
      return onChangeEmails((prevEmails) => ({
        ...prevEmails,
        tokens: uniq([...prevEmails.tokens, ...newTokens]),
        text: rest,
      }));
    }

    return onChangeEmails((prevEmails) => ({
      ...prevEmails,
      text,
    }));
  };

  const renderToken = (index: number, isSelected: boolean) => (
    <StyledEmailInputTokenDiv $isSelected={isSelected}>{emails.tokens[index]}</StyledEmailInputTokenDiv>
  );

  const ref = useRef<TokensEditor>(null);

  return (
    <StyledEmailsInputWrapper>
      <StyledBoxedInput
        isRequired
        label={<Trans id="settings-teammate-new-enter-email-emails-input">Teammate email(s)</Trans>}
        getInput={() => ref.current?.getInputElement()}
      >
        {() => (
          <TokensEditor
            ref={ref}
            text={emails.text}
            onTextChange={onChangeText}
            tokenIds={emails.tokens}
            onRemoveTokensClick={onRemoveTokensClick}
            renderToken={renderToken}
            placeholder={localize(
              defineMessage({
                id: 'settings-teammates-new-enter-email-emails-placeholder',
                message: 'Separate emails with a space',
              }),
            )}
          />
        )}
      </StyledBoxedInput>
      {help}
    </StyledEmailsInputWrapper>
  );
};
